import { Badge, Box, Flex, Text, VStack } from '@elkaso-app/web-design';
import {
  PriceImportRow,
  priceImportRowsEndpointIdentifier,
  priceImportsEndpointIdentifier,
  useUpdatePriceImportProposalApi,
} from 'apis/price-imports';
import { FamilyProposalCard } from 'pages/price-imports/components/family-proposal-card';
import { aggregateProposals, FamilyProposal, NamedProposal } from 'pages/price-imports/utils/aggregate-proposals';
import { useMemo } from 'react';
import { useQueryClient } from 'react-query';

export interface ApprovalsPanelProps {
  runId: number;
  rows: PriceImportRow[];
  readOnly: boolean;
  onReviewRows: (rowNumbers: number[]) => void;
}

const Section = ({
  title,
  hint,
  count,
  children,
}: {
  title: string;
  hint: string;
  count: number;
  children: React.ReactNode;
}) => (
  <Box mb='18px'>
    <Flex align='baseline' gap='8px' mb='2px'>
      <Text fontSize='sm' fontWeight='semibold'>
        {title}
      </Text>
      <Badge variant='subtle'>{count}</Badge>
    </Flex>
    <Text fontSize='xs' color='gray.500' mb='8px'>
      {hint}
    </Text>
    {count === 0 ? (
      <Text fontSize='sm' color='gray.400'>
        None.
      </Text>
    ) : (
      <VStack align='stretch' spacing='6px'>
        {children}
      </VStack>
    )}
  </Box>
);

const SimpleCard = ({ item }: { item: NamedProposal }) => (
  <Flex
    align='center'
    justify='space-between'
    border='1px solid'
    borderColor='gray.200'
    borderRadius='8px'
    bg='white'
    px='10px'
    py='7px'>
    <Text fontSize='sm'>{item.label}</Text>
    <Text fontSize='xs' color='gray.500'>
      {item.rowIds.length} row{item.rowIds.length === 1 ? '' : 's'}
    </Text>
  </Flex>
);

/**
 * What committing this run would create, and the decisions that gate it.
 *
 * Listed in the order the backend's commit preamble creates them — dictionary
 * values, brands, families — because a proposed variant needs its family and
 * brand to exist first. Everything is folded by name across rows: the real DEEP
 * list proposed twelve families across twenty lines, and asking twenty
 * questions with twelve answers is the thing this screen exists to avoid.
 *
 * There is deliberately no "approve everything" button. Approving is per row,
 * from the review grid; this screen is where an operator understands what those
 * approvals will actually create.
 */
export const ApprovalsPanel = ({ runId, rows, readOnly, onReviewRows }: ApprovalsPanelProps) => {
  const queryClient = useQueryClient();
  const proposals = useMemo(() => aggregateProposals(rows), [rows]);

  const { mutateAsync: updateProposal, isLoading } = useUpdatePriceImportProposalApi({
    onSuccess: () => {
      queryClient.invalidateQueries([priceImportRowsEndpointIdentifier, runId]);
      queryClient.invalidateQueries([priceImportsEndpointIdentifier, runId]);
    },
  });

  const setCategory = (family: FamilyProposal, catalogCategoryId: number) =>
    updateProposal({ runId, kind: 'family', key: family.universalName, catalogCategoryId }).catch(() => undefined);

  if (proposals.total === 0) {
    return (
      <Text fontSize='sm' color='gray.500'>
        This import creates nothing new — every row matched an existing product.
      </Text>
    );
  }

  return (
    <Box maxW='760px'>
      {proposals.blockedFamilies.length > 0 && (
        <Box bg='orange.50' border='1px solid' borderColor='orange.200' borderRadius='8px' p='10px' mb='16px'>
          <Text fontSize='sm' color='orange.800'>
            {proposals.blockedFamilies.length} new{' '}
            {proposals.blockedFamilies.length === 1 ? 'family needs' : 'families need'} a category before this import
            can commit. A family cannot exist without one.
          </Text>
        </Box>
      )}

      <Section
        title='New attribute values'
        hint='Added to the shared dictionary, so every future product can use them.'
        count={proposals.dictionaryValues.length}>
        {proposals.dictionaryValues.map((item) => (
          <SimpleCard key={item.key} item={item} />
        ))}
      </Section>

      <Section title='New brands' hint='Added to the brand dictionary.' count={proposals.brands.length}>
        {proposals.brands.map((item) => (
          <SimpleCard key={item.key} item={item} />
        ))}
      </Section>

      <Section
        title='New families'
        hint='Created first, because the new products below sit inside them.'
        count={proposals.families.length}>
        {proposals.families.map((family) => (
          <FamilyProposalCard
            key={family.key}
            family={family}
            readOnly={readOnly}
            isSaving={isLoading}
            onSetCategory={setCategory}
            onReviewRows={onReviewRows}
          />
        ))}
      </Section>

      <Section
        title='New products'
        hint='One per row. Created inside the families above when the import commits.'
        count={proposals.variantRowIds.length}>
        <Text fontSize='sm' color='gray.600'>
          {proposals.variantRowIds.length} rows would create a product that does not exist yet.
        </Text>
      </Section>
    </Box>
  );
};
